import { DefaultButton, Dropdown, IDropdownOption, MessageBar, Pivot, PivotItem, PrimaryButton, Spinner, SpinnerSize, Stack, useTheme } from '@fluentui/react';
import React from 'react';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { buildGlobalSettingsPatch, IGlobalStorefrontSettings, IStorefrontSection, IStorefrontSettings, ShowLinkedItemsMode } from '../../../../models/Storefront';
import { useGetVendorsQuery } from '../../../../store/Api';
import { useGetOrganizationQuery } from '../../../../store/endpoints/OrganizationsEndpoints';
import { useGetGlobalStorefrontSettingsQuery, usePatchGlobalStorefrontSettingsMutation, useGetStorefrontsQuery, useRunProductRecommendationsMutation } from '../../../../store/endpoints/StorefrontSettingsEndpoints';
import useCan, { PRODUCT_RECOMMENDATIONS_ADMIN } from '../../../../permissions/ability';
import { useGetStorefrontSectionsQuery, useSaveStorefrontSectionsMutation } from '../../../../store/endpoints/StorefrontSectionEndpoints';
import { RootState } from '../../../../store/rootReducer';
import ToggleField from '../../../../components/Common/ToggleField';
import DropdownField from '../../../../components/Common/DropdownField';
import NumberField from '../../../../components/Inputs/NumberField';
import MarkupTypeSelection from '../../../../components/Storefront/MarkupTypeSelection';
import Setting from '../../../../components/Common/Setting';
import { useUrlTabState } from '../../../../hooks/useUrlState';
import FulfillmentForm from '../../../../components/Storefront/Fulfillment/FulfillmentForm';
import StorefrontSectionsEditor from '../../../../components/Storefront/Editor/Sections/StorefrontSectionsEditor';

const DEBOUNCE_MS = 3000;

// ─── Global Sections Editor ───────────────────────────────────────────────────

const GlobalSectionsEditor: React.FC = () => {
    const storefronts = useGetStorefrontsQuery();
    const sectionsQuery = useGetStorefrontSectionsQuery('global');
    const [saveStorefrontSections] = useSaveStorefrontSectionsMutation();

    const [sections, setSections] = React.useState<IStorefrontSection[]>([]);
    const [previewStorefrontId, setPreviewStorefrontId] = React.useState<string | undefined>();
    const [editMode, setEditMode] = React.useState(false);
    const [saving, setSaving] = React.useState(false);
    /*
     * Same rule as the per-storefront page editor: a settings deep link turns editing on, because
     * the section editors only render here in edit mode. Entering only — leaving is "Save Sections",
     * which writes, so it stays deliberate.
     */
    React.useEffect(() => {
        if (new URLSearchParams(window.location.search).get('focus')) {
            setEditMode(true);
        }
    }, []);

    React.useEffect(() => {
        if (sectionsQuery.data) {
            const sorted = [...sectionsQuery.data.items].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
            setSections(sorted);
        }
    }, [sectionsQuery.data]);

    // Auto-select the first storefront for preview if none chosen yet
    React.useEffect(() => {
        if (!previewStorefrontId && storefronts.data?.items.length) {
            setPreviewStorefrontId(storefronts.data.items[0].id ?? undefined);
        }
    }, [storefronts.data, previewStorefrontId]);

    const storefrontOptions: IDropdownOption[] = (storefronts.data?.items ?? []).map(sf => ({
        key: sf.id ?? '',
        text: sf.name ?? '',
    }));

    const handleEditModeToggle = async () => {
        if (editMode) {
            setSaving(true);
            try {
                const toSave = sections.map((s, i) => {
                    const { isNew, ...rest } = s;
                    return { ...rest, sortOrder: i, id: isNew ? undefined : s.id };
                });
                await saveStorefrontSections({ sections: toSave, storefront: 'global' }).unwrap();
                toast.success('Sections saved');
                setEditMode(false);
            } catch {
                toast.error('Failed to save sections');
            } finally {
                setSaving(false);
            }
        } else {
            setEditMode(true);
        }
    };

    const handleCancel = () => {
        if (sectionsQuery.data) {
            const sorted = [...sectionsQuery.data.items].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
            setSections(sorted);
        }
        setEditMode(false);
    };

    if (sectionsQuery.isLoading) {
        return <Spinner size={SpinnerSize.medium} label='Loading sections...' />;
    }

    // The WYSIWYG StorefrontSectionsEditor only uses storefrontSettings.id for product previews.
    const previewSettings = { id: previewStorefrontId } as IStorefrontSettings;

    return (
        <div style={{ marginTop: '1em' }}>
            <MessageBar>
                Global sections appear on every storefront that does not ignore global sections. Product previews require a specific storefront context — choose one below to see real products.
            </MessageBar>
            <Stack horizontal verticalAlign='end' tokens={{ childrenGap: '1em' }} style={{ marginTop: '1em', marginBottom: '1em' }}>
                <div style={{ width: 240 }}>
                    <Dropdown
                        label='Preview products from'
                        placeholder='Select a storefront'
                        selectedKey={previewStorefrontId ?? ''}
                        options={storefrontOptions}
                        onChange={(_, opt) => setPreviewStorefrontId(opt?.key as string)}
                        disabled={storefronts.isLoading}
                    />
                </div>
                <PrimaryButton
                    text={editMode ? 'Save Sections' : 'Edit Sections'}
                    iconProps={{ iconName: editMode ? 'Save' : 'Edit' }}
                    onClick={handleEditModeToggle}
                    disabled={saving}
                />
                {editMode && (
                    <DefaultButton
                        text='Discard Changes'
                        onClick={handleCancel}
                        disabled={saving}
                    />
                )}
            </Stack>
            <StorefrontSectionsEditor
                storefrontSettings={previewSettings}
                editMode={editMode}
                sections={sections}
                onSectionsChange={setSections}
                emptyMessage='No sections configured. Click "Edit Sections" to add one.'
            />
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────

const filterStrategyOptions = [
    { key: 'exclude', text: 'Exclude' },
    { key: 'include', text: 'Include' },
];

const SHOW_LINKED_ITEMS_OPTIONS: IDropdownOption[] = [
    { key: 'all', text: 'All Products' },
    { key: 'select', text: 'Select Products' },
    { key: 'disabled', text: 'Disabled' },
];

const StorefrontGlobalProductSettingsPage: React.FC = () => {
    // In the URL so a documentation link can land on the tab holding the setting it describes.
    const [selectedTab, setSelectedTab] = useUrlTabState('settings');
    const theme = useTheme();
    const globalSettingsQuery = useGetGlobalStorefrontSettingsQuery();
    const [patchGlobalSettings] = usePatchGlobalStorefrontSettingsMutation();

    const [localSettings, setLocalSettingsRaw] = React.useState<IGlobalStorefrontSettings>();
    const localSettingsRef = React.useRef<IGlobalStorefrontSettings>();
    const originalSettingsRef = React.useRef<IGlobalStorefrontSettings>();
    const debounceSaveRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

    const selectedOrganization = useSelector((state: RootState) => state.settings.selectedOrganization);
    const organization = useGetOrganizationQuery(selectedOrganization ?? '', { skip: !selectedOrganization || selectedOrganization === '' });
    const vendors = useGetVendorsQuery(organization.data?.defaultCatalog ?? '', { skip: !organization.data?.defaultCatalog });

    // Manual recommendations run. The permission is stripped for orgs without the entitlement, so the
    // button simply isn't rendered for them; the API re-checks the entitlement regardless.
    const canRecommendations = useCan(PRODUCT_RECOMMENDATIONS_ADMIN);
    const [runRecommendations, runRecommendationsState] = useRunProductRecommendationsMutation();
    const queueRecommendationsRun = async () => {
        try {
            const result = await runRecommendations().unwrap();
            if (result.success) toast.success(result.message ?? 'Recommendations run queued');
            else toast.error(result.message ?? 'Could not queue the recommendations run');
        } catch {
            toast.error('Could not queue the recommendations run');
        }
    };

    const setLocalSettings = React.useCallback((s: IGlobalStorefrontSettings) => {
        localSettingsRef.current = s;
        setLocalSettingsRaw(s);
    }, []);

    React.useEffect(() => {
        if (globalSettingsQuery.data && !originalSettingsRef.current) {
            setLocalSettings(globalSettingsQuery.data);
            originalSettingsRef.current = globalSettingsQuery.data;
        }
    }, [globalSettingsQuery.data, setLocalSettings]);

    React.useEffect(() => {
        return () => {
            if (debounceSaveRef.current) {
                clearTimeout(debounceSaveRef.current);
                if (localSettingsRef.current && originalSettingsRef.current) {
                    const patch = buildGlobalSettingsPatch(originalSettingsRef.current, localSettingsRef.current);
                    if (Object.keys(patch).length > 0) {
                        patchGlobalSettings(patch);
                    }
                }
            }
        };
    }, []);

    const doSave = React.useCallback(async (current: IGlobalStorefrontSettings) => {
        if (!originalSettingsRef.current) return;
        const patch = buildGlobalSettingsPatch(originalSettingsRef.current, current);
        if (Object.keys(patch).length === 0) return;
        try {
            await patchGlobalSettings(patch).unwrap();
            originalSettingsRef.current = { ...originalSettingsRef.current, ...patch };
            toast.success('Settings saved');
        } catch {
            toast.error('Failed to save settings');
        }
    }, [patchGlobalSettings]);

    const scheduleSave = React.useCallback(() => {
        if (debounceSaveRef.current) clearTimeout(debounceSaveRef.current);
        debounceSaveRef.current = setTimeout(() => {
            if (localSettingsRef.current) doSave(localSettingsRef.current);
        }, DEBOUNCE_MS);
    }, [doSave]);

    if (globalSettingsQuery.isLoading || !localSettings) {
        return <div>Loading...</div>;
    }

    const handleToggleChange = (ev?: React.FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean, name?: string) => {
        if (!name) return;
        const updated = { ...(localSettingsRef.current ?? localSettings), [name]: checked ?? false };
        setLocalSettings(updated);
        scheduleSave();
    };

    const handleVendorChange = async (ev: React.FormEvent<HTMLDivElement>, item?: IDropdownOption) => {
        if (!item) return;
        const current = localSettingsRef.current ?? localSettings;
        const currentVendors = current.storefrontVendors ?? [];
        const newVendors = item.selected
            ? [...currentVendors, item.key as string]
            : currentVendors.filter(v => v !== item.key);
        setLocalSettings({ ...current, storefrontVendors: newVendors });
        try {
            await patchGlobalSettings({ storefrontVendors: newVendors }).unwrap();
            if (originalSettingsRef.current) {
                originalSettingsRef.current = { ...originalSettingsRef.current, storefrontVendors: newVendors };
            }
            toast.success('Settings saved');
        } catch {
            toast.error('Failed to save settings');
        }
    };

    return <>
        <h1>Storefront Products</h1>
        <Pivot selectedKey={selectedTab} onLinkClick={(item) => setSelectedTab(item?.props.itemKey)}>
            <PivotItem headerText='Settings' itemKey='settings'>
                <Stack tokens={{ childrenGap: '1em' }}>
                    <h2>Markup</h2>
                    <NumberField
                        settingId='storefront.global.products.defaultMarkup'
                        label='Default Markup'
                        decimal={true}
                        value={localSettings.defaultMarkup}
                        onChange={(v) => {
                            const updated = { ...(localSettingsRef.current ?? localSettings), defaultMarkup: v };
                            setLocalSettings(updated);
                            doSave(updated);
                        }}
                    />
                    <Setting id='storefront.global.products.defaultMarkupType'>
                        <MarkupTypeSelection
                            isLowestLevel={true}
                            value={localSettings.defaultMarkupType}
                            onChange={(v) => {
                                const updated = { ...(localSettingsRef.current ?? localSettings), defaultMarkupType: v };
                                setLocalSettings(updated);
                                scheduleSave();
                            }}
                            label='Default Markup Type'
                        />
                    </Setting>
                    <h2>Vendor Filters</h2>
                    <Setting id='storefront.global.products.storefrontVendors'>
                        <Dropdown
                            placeholder='Select Vendors'
                            multiSelect={true}
                            label='Storefront Vendor Filter'
                            selectedKeys={localSettings.storefrontVendors}
                            options={vendors?.data?.items?.map(vendor => ({ key: vendor, text: vendor })) ?? []}
                            onChange={handleVendorChange}
                            disabled={vendors.isLoading || vendors.isFetching}
                        />
                    </Setting>
                    <Setting id='storefront.global.products.vendorFilterStrategy'>
                        <Dropdown
                            placeholder='Select Filter Strategy'
                            multiSelect={false}
                            label='Vendor Filter Strategy'
                            selectedKey={localSettings.vendorFilterStrategy ?? 'include'}
                            options={filterStrategyOptions}
                            onChange={(e, v) => {
                                const updated = { ...(localSettingsRef.current ?? localSettings), vendorFilterStrategy: v?.key.toString() };
                                setLocalSettings(updated);
                                scheduleSave();
                            }}
                        />
                    </Setting>
                    <h2>Product Display</h2>
                    <ToggleField
                        settingId='storefront.global.products.showMfpOnProducts'
                        toolTip='If enabled, the Manufacturer Part Number (MFP) will be displayed on the Product Details page in the storefront.'
                        inline={true}
                        label='Show MFP on Product Details'
                        checked={localSettings.showMfpOnProducts}
                        name='showMfpOnProducts'
                        onChange={handleToggleChange}
                    />
                    <div>
                        <DropdownField
                            settingId='storefront.global.products.showLinkedItemsMode'
                            label='Show Linked Items on Storefronts'
                            selectedKey={localSettings.showLinkedItemsMode ?? 'all'}
                            options={SHOW_LINKED_ITEMS_OPTIONS}
                            onChange={(_e, option) => {
                                const updated = { ...(localSettingsRef.current ?? localSettings), showLinkedItemsMode: option?.key as ShowLinkedItemsMode };
                                setLocalSettings(updated);
                                doSave(updated);
                            }}
                        />
                        <div style={{ color: theme.palette.neutralSecondary, fontSize: 12, marginTop: '0.5em' }}>
                            Controls whether products&apos; linked items appear on storefront product pages. &apos;Select Products&apos; shows them only for products you enable individually.
                        </div>
                    </div>
                    {canRecommendations && <div>
                        <h2>Recommendations</h2>
                        <DefaultButton
                            text={runRecommendationsState.isLoading ? 'Queueing…' : 'Recalculate Recommendations Now'}
                            iconProps={{ iconName: 'Sync' }}
                            disabled={runRecommendationsState.isLoading}
                            onClick={queueRecommendationsRun}
                        />
                        <div style={{ color: theme.palette.neutralSecondary, fontSize: 12, marginTop: '0.5em' }}>
                            Recomputes the &quot;Frequently Added Together&quot; and &quot;More Like This&quot; rows for every
                            storefront in this organization. This normally runs on a schedule during low-usage hours;
                            use this to pick up new products or orders straight away. The run happens in the
                            background and can take a few minutes, so the rows will not change the instant it finishes
                            queueing.
                        </div>
                    </div>}
                </Stack>
            </PivotItem>
            <PivotItem headerText='Sections' itemKey='sections'>
                <GlobalSectionsEditor />
            </PivotItem>
            <PivotItem headerText='Fulfillment' itemKey='fulfillment'>
                <FulfillmentForm
                    globalSettings={localSettings}
                    onGlobalSettingsChange={(s) => { setLocalSettings(s); scheduleSave(); }}
                />
            </PivotItem>
        </Pivot>
    </>
}

export default StorefrontGlobalProductSettingsPage;